import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface PaginatedProcurementsResult {
  data: any[];
  count: number;
  totalPages: number;
  currentPage: number;
}

export interface ProcurementSummary {
  totalCount: number;
  statusCounts: Record<string, number>;
  totalAmount: number;
  recentCount: number;
}

export const usePaginatedProcurements = (page: number = 1, limit: number = 20) => {
  const { user, userRole } = useAuth();
  
  return useQuery({
    queryKey: ['procurements-paginated', user?.id, userRole, page, limit],
    queryFn: async (): Promise<PaginatedProcurementsResult> => {
      const offset = (page - 1) * limit;
      
      let query = supabase
        .from('procurements')
        .select(`
          *,
          requestor:requestor_id(
            id,
            first_name,
            last_name
          )
        `, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      // Filter by current user for sellers
      if (userRole === 'seller' && user?.id) {
        query = query.eq('requestor_id', user.id);
      }

      const { data, error, count } = await query;

      if (error) {
        console.error('Error fetching paginated procurements:', error);
        throw error;
      }

      const totalPages = Math.ceil((count || 0) / limit);

      return {
        data: data || [],
        count: count || 0,
        totalPages,
        currentPage: page
      };
    },
    staleTime: 1000 * 60 * 2, // 2 minutes cache
  });
};

export const useProcurementSummary = () => {
  const { user, userRole } = useAuth();
  
  return useQuery({
    queryKey: ['procurement-summary', user?.id, userRole],
    queryFn: async (): Promise<ProcurementSummary> => {
      // Base query for counts
      let countQuery = supabase
        .from('procurements')
        .select('status, total_amount, created_at', { count: 'exact' });

      // Filter by current user for sellers
      if (userRole === 'seller' && user?.id) {
        countQuery = countQuery.eq('requestor_id', user.id);
      }

      const { data, error, count } = await countQuery;

      if (error) {
        console.error('Error fetching procurement summary:', error);
        throw error;
      }

      // Calculate status counts and total amount
      const statusCounts: Record<string, number> = {};
      let totalAmount = 0;
      let recentCount = 0;
      
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      data?.forEach(procurement => {
        // Count by status
        const status = procurement.status || 'unknown';
        statusCounts[status] = (statusCounts[status] || 0) + 1;
        
        // Sum total amounts
        if (procurement.total_amount) {
          totalAmount += procurement.total_amount;
        }
        
        // Count recent procurements (last 7 days)
        if (procurement.created_at && new Date(procurement.created_at) >= sevenDaysAgo) {
          recentCount++;
        }
      });

      return {
        totalCount: count || 0,
        statusCounts,
        totalAmount,
        recentCount
      };
    },
    staleTime: 1000 * 60 * 5, // 5 minutes cache for summary
  });
};